import { FC, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import { Row, Col, message } from "antd";
import {
  Controller,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from "react-hook-form";
import { useHandleForm } from "../../../hooks/useHandleForm";
import SmsSettingsListFormUsers from "./users";
import {
  AutoComplete,
  FormBlock,
  FormField,
  IButton,
  Skeleton,
} from "../../../ui-kit";
import smsSettings from "../../../store/smsSettings";

import styles from "../SmsSettings.module.scss";
import {
  SmsSettingClientGroupClientType,
  SmsSettingClientGroupType,
} from "../SmsSettingsDistribution";

const { SelectSkeleton } = Skeleton;

const SmsSettingsListForm: FC<{ record?: SmsSettingClientGroupType }> =
  observer(({ record }) => {
    const { distributionListFormIsSending } = smsSettings;
    const initialValues = {
      ...toJS(record),
      eventSeverity: null,
    };

    const {
      register,
      handleSubmit,
      watchAllField,
      isFocused,
      handleBlur,
      handleFocus,
      control,
      setValue,
    } = useHandleForm(null, initialValues, {});

    const [isLoadingEventSeverity, setIsLoadingEventSeverity] = useState(true);

    const loadSeverity = async () => {
      try {
        if (initialValues.eventSeverityId) {
          const res = await smsSettings.findSmsIncidentsParamsById(
            1,
            0,
            initialValues.eventSeverityId,
          );
          const eventSeverity = res.data.items[0];
          setValue("eventSeverity", {
            ...eventSeverity,
            value: eventSeverity.name,
          });
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoadingEventSeverity(false);
      }
    };

    useEffect(() => {
      loadSeverity();
    }, [initialValues.eventSeverityId]);

    const {
      fields: clientFields,
      append: appendClient,
      remove: removeClient,
    } = useFieldArray({
      control,
      name: "clients",
      keyName: "uuid",
    }) as unknown as {
      fields: Array<SmsSettingClientGroupClientType & { uuid: string }>;
      append: UseFieldArrayAppend<SmsSettingClientGroupClientType>;
      remove: UseFieldArrayRemove;
    };

    const onSubmit = (values) => {
      const clients = values.clients?.map((client) => {
        return {
          ...client,
          from: client.date ? client.date[0] : "",
          to: client.date ? client.date[1] : "",
        };
      });
      delete values.eventSeverity;
      smsSettings.saveSmsDistributionList(record.id, {
        ...values,
        clients: clients,
      });
    };

    const mapEventSeverities = (options) => {
      return options.map((item) => {
        return { ...item, label: item.name };
      });
    };

    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.SmsSettingsForm}
      >
        <FormBlock>
          <Row gutter={[15, 20]}>
            <Col span={12}>
              <FormField
                label="Название"
                name="name"
                register={register}
                type="text"
                isFocused={isFocused.name}
                currentValue={watchAllField.name}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Col>
            <Col span={12}>
              {isLoadingEventSeverity ? (
                <SelectSkeleton />
              ) : (
                <Controller
                  control={control}
                  name={`eventSeverity.value`}
                  render={({ field }) => (
                    <AutoComplete
                      field={field}
                      label="Тип события для рассылки"
                      required
                      getData={smsSettings.findSmsIncidentsParamsByName}
                      mapOptions={mapEventSeverities}
                      defaultSearchString={""}
                      onSelect={(opt: any) => {
                        setValue("eventSeverity", opt);
                        setValue("eventSeverityId", opt.id);
                      }}
                      onChange={(val) => {
                        if (!val) {
                          setValue("eventSeverity", null);
                          setValue(`eventSeverityId`, null);
                        }
                      }}
                    />
                  )}
                />
              )}
            </Col>
            <Col span={24}>
              <SmsSettingsListFormUsers
                register={register}
                control={control}
                fields={clientFields}
                append={appendClient}
                remove={removeClient}
                watch={watchAllField}
              />
            </Col>
            <Col span={24} className={styles.SmsSettingsFormButton}>
              <IButton disabled={distributionListFormIsSending.value}>
                Сохранить группу
              </IButton>
            </Col>
          </Row>
        </FormBlock>
      </form>
    );
  });

export default SmsSettingsListForm;
